import type { BankingCapabilities, BankingPermissionTier } from '../types/jack-henry-banking'
import { FULL_BANKING_CAPABILITIES } from '../types/jack-henry-banking'
import type { IntegrationProviderId } from '../types/integration-provider-id'
import { BANKING_SCOPE } from '../types/banking-scopes'

function addJxTierScopes(
  allowed: Set<string>,
  tier: BankingPermissionTier | undefined,
  readScope: string,
  writeScope: string
): void {
  if (!tier || tier === 'none') {
    return
  }
  allowed.add(readScope)
  if (tier === 'write') {
    allowed.add(writeScope)
  }
}

/**
 * JXChange + Online Banking Admin (RDC) scopes only — excludes Synergy document scopes.
 * Used when composing OAuth secret grants from tier selections.
 */
export function bankingProductScopesFromCapabilities(
  capabilities: BankingCapabilities
): string[] {
  const allowed = new Set<string>()

  addJxTierScopes(
    allowed,
    capabilities.customers,
    BANKING_SCOPE.CUSTOMER_READ,
    BANKING_SCOPE.CUSTOMER_WRITE
  )
  addJxTierScopes(
    allowed,
    capabilities.accounts,
    BANKING_SCOPE.ACCOUNT_READ,
    BANKING_SCOPE.ACCOUNT_WRITE
  )
  addJxTierScopes(
    allowed,
    capabilities.transfers,
    BANKING_SCOPE.TRANSFER_READ,
    BANKING_SCOPE.TRANSFER_WRITE
  )
  addJxTierScopes(
    allowed,
    capabilities.transactions,
    BANKING_SCOPE.TRANSACTIONS_READ,
    BANKING_SCOPE.TRANSACTIONS_WRITE
  )
  addJxTierScopes(allowed, capabilities.loan, BANKING_SCOPE.LOAN_READ, BANKING_SCOPE.LOAN_WRITE)

  const rq = capabilities.rdcRequests
  if (rq === 'read' || rq === 'write') {
    allowed.add(BANKING_SCOPE.RDC_REQUESTS_READ)
  }

  const ra = capabilities.rdcAccounts
  if (ra === 'read' || ra === 'write') {
    allowed.add(BANKING_SCOPE.RDC_ACCOUNTS_READ)
    if (ra === 'write') {
      allowed.add(BANKING_SCOPE.RDC_ACCOUNTS_WRITE)
    }
  }

  const rv = capabilities.rdcVelocity
  if (rv === 'read' || rv === 'write') {
    allowed.add(BANKING_SCOPE.RDC_VELOCITY_READ)
    if (rv === 'write') {
      allowed.add(BANKING_SCOPE.RDC_VELOCITY_WRITE)
    }
  }

  return Array.from(allowed)
}

function collectCapabilityScopeSet(
  capabilities: BankingCapabilities | undefined,
  options?: { includeDocumentScopes?: boolean }
): Set<string> {
  const allowed = new Set<string>()
  const includeDocuments = options?.includeDocumentScopes !== false
  if (!capabilities) {
    if (includeDocuments) {
      allowed.add(BANKING_SCOPE.DOCUMENTS_READ)
      allowed.add(BANKING_SCOPE.DOCUMENTS_WRITE)
    }
    return allowed
  }

  for (const s of bankingProductScopesFromCapabilities(capabilities)) {
    allowed.add(s)
  }

  if (includeDocuments) {
    allowed.add(BANKING_SCOPE.DOCUMENTS_READ)
    allowed.add(BANKING_SCOPE.DOCUMENTS_WRITE)
  }
  return allowed
}

export function maxScopesForIntegrationProviders(
  providerIds: IntegrationProviderId[],
  capabilities: BankingCapabilities = FULL_BANKING_CAPABILITIES
): string[] {
  const allowed = new Set<string>()
  const hasSilverlake = providerIds.includes('jack_henry_silverlake')
  const hasSynergy = providerIds.includes('jack_henry_synergy')

  if (hasSilverlake) {
    for (const s of bankingProductScopesFromCapabilities(capabilities)) {
      allowed.add(s)
    }
  }

  if (hasSynergy) {
    allowed.add(BANKING_SCOPE.DOCUMENTS_READ)
    allowed.add(BANKING_SCOPE.DOCUMENTS_WRITE)
  }

  if (providerIds.includes('equifax_prequal')) {
    allowed.add(BANKING_SCOPE.PREQUAL_READ)
    allowed.add(BANKING_SCOPE.PREQUAL_WRITE)
  }

  if (providerIds.includes('equifax_dit')) {
    allowed.add(BANKING_SCOPE.HARDPULL_READ)
    allowed.add(BANKING_SCOPE.HARDPULL_WRITE)
  }

  return Array.from(allowed)
}

export function allScopesAllowedForCapabilities(
  capabilities: BankingCapabilities | undefined
): string[] {
  return Array.from(collectCapabilityScopeSet(capabilities))
}

export function jxchangeCoreBaasScopesFromCapabilities(
  capabilities: BankingCapabilities
): string[] {
  const out: string[] = []
  const collect = (tier: BankingPermissionTier, read: string, write: string) => {
    if (tier === 'none') {
      return
    }
    out.push(read)
    if (tier === 'write') {
      out.push(write)
    }
  }
  collect(capabilities.customers, BANKING_SCOPE.CUSTOMER_READ, BANKING_SCOPE.CUSTOMER_WRITE)
  collect(capabilities.accounts, BANKING_SCOPE.ACCOUNT_READ, BANKING_SCOPE.ACCOUNT_WRITE)
  collect(capabilities.transfers, BANKING_SCOPE.TRANSFER_READ, BANKING_SCOPE.TRANSFER_WRITE)
  collect(
    capabilities.transactions,
    BANKING_SCOPE.TRANSACTIONS_READ,
    BANKING_SCOPE.TRANSACTIONS_WRITE
  )
  collect(capabilities.loan, BANKING_SCOPE.LOAN_READ, BANKING_SCOPE.LOAN_WRITE)
  return out
}

export function scopesAllowedForCapabilities(
  capabilities: BankingCapabilities | undefined,
  requested: string[]
): string[] {
  if (requested.length === 0) {
    return []
  }
  const allowed = collectCapabilityScopeSet(capabilities)
  return requested.filter((s) => allowed.has(s))
}

export function intersectWithSecretGrants(
  capabilities: BankingCapabilities | undefined,
  requested: string[],
  secretGrants: string[]
): string[] {
  const fromCaps = scopesAllowedForCapabilities(capabilities, requested)
  const grantSet = new Set(secretGrants)
  return fromCaps.filter((s) => grantSet.has(s))
}

export function tokenScopesFromSecretOnly(
  requested: string[],
  secretGrants: string[]
): string[] {
  if (requested.length === 0) {
    return [...new Set(secretGrants)]
  }
  const grantSet = new Set(secretGrants)
  return requested.filter((s) => grantSet.has(s))
}
